/**
 * SAGE Orders Settings Modal
 * 
 * SAGE-specific order settings (customerMode, defaults, etc.)
 * Saves to: TenantConfig.key='orders' dto.settings.order
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Modal, FormLayout, TextField, Select, BlockStack, Card, Text, InlineStack, Checkbox } from '@shopify/polaris';
import { useLockBodyScroll } from '../../../hooks/useLockBodyScroll';

export function SageOrdersSettingsModal({ open, onClose, onSave, config }) {
  useLockBodyScroll(open);
  
  const initial = useMemo(() => ({
    order: config?.order || {
      customerMode: 'fixed',
      fixedCustomerNumber: 'SHOP-0001',
      createStrategy: 'email_hash',
      customerPrefix: 'SHOP-',
      customerDefaults: {
        groupCode: 'CAN',
        accountSet: 'CDNS1',
        priceList: 'CAN'
      },
      defaultLocation: '1',
      defaultPriceList: 'CAN',
      termsCode: '4',
      taxGroup: 'BCCAN',
      paymentCode: 'CASH',
      shipToPrefix: 'S',
      shippingChargeCode: 'SHIP01',
      giftCardChargeCode: 'MC0002',
      prepayment: {
        enabled: false,
        batchPrefix: 'WEB',
        batchFrequency: 'weekly'
      }
    }
  }), [config]);

  const [local, setLocal] = useState(initial);
  
  useEffect(() => {
    if (open) setLocal(initial);
  }, [open, initial]);

  const handleSave = () => {
    onSave?.('orders', { order: local.order });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="SAGE Order Settings"
      primaryAction={{ content: 'Save Configuration', onAction: handleSave }}
      secondaryActions={[{ content: 'Cancel', onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="600">
          <Card sectioned>
            <BlockStack gap="loose">
              <Text variant="headingMd" as="h3">Customer Configuration</Text>
              
              <Select
                label="Customer Mode"
                options={[
                  { label: 'Use Fixed Customer (Simple)', value: 'fixed' },
                  { label: 'Create Individual Customers (Advanced)', value: 'create' }
                ]}
                value={local.order.customerMode || 'fixed'}
                onChange={(value) => setLocal(prev => ({
                  ...prev,
                  order: { ...prev.order, customerMode: value }
                }))}
                helpText={local.order.customerMode === 'fixed' ? 
                  'All orders go to one customer with unique ship-to locations' :
                  'Each Shopify customer becomes a SAGE customer (requires customers service)'}
              />
              
              {local.order.customerMode === 'fixed' && (
                <TextField
                  label="Fixed Customer Number"
                  value={local.order.fixedCustomerNumber || ''}
                  onChange={(value) => setLocal(prev => ({
                    ...prev,
                    order: { ...prev.order, fixedCustomerNumber: value }
                  }))}
                  placeholder="SHOP-0001"
                  helpText="SAGE customer number for all orders"
                />
              )}
              
              {local.order.customerMode === 'create' && (
                <>
                  <Select
                    label="Customer ID Strategy"
                    options={[
                      { label: 'Email Hash', value: 'email_hash' },
                      { label: 'Shopify Customer ID', value: 'shopify_id' }
                    ]}
                    value={local.order.createStrategy || 'email_hash'}
                    onChange={(value) => setLocal(prev => ({
                      ...prev,
                      order: { ...prev.order, createStrategy: value }
                    }))}
                  />
                  
                  <TextField
                    label="Customer Prefix"
                    value={local.order.customerPrefix || ''}
                    onChange={(value) => setLocal(prev => ({
                      ...prev,
                      order: { ...prev.order, customerPrefix: value }
                    }))}
                    placeholder="SHOP-"
                  />
                </>
              )}

              <Text variant="headingSm" as="h4" fontWeight="semibold">Customer Defaults</Text>
              
              <InlineStack gap="400">
                <div style={{ flex: 1 }}>
                  <TextField
                    label="Group Code"
                    value={local.order.customerDefaults?.groupCode || 'CAN'}
                    onChange={(value) => setLocal(prev => ({
                      ...prev,
                      order: { 
                        ...prev.order, 
                        customerDefaults: { 
                          ...prev.order.customerDefaults, 
                          groupCode: value 
                        } 
                      }
                    }))}
                    placeholder="CAN"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <TextField
                    label="Account Set"
                    value={local.order.customerDefaults?.accountSet || 'CDNS1'}
                    onChange={(value) => setLocal(prev => ({
                      ...prev,
                      order: { 
                        ...prev.order, 
                        customerDefaults: { 
                          ...prev.order.customerDefaults, 
                          accountSet: value 
                        } 
                      }
                    }))}
                    placeholder="CDNS1"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <TextField
                    label="Price List"
                    value={local.order.customerDefaults?.priceList || 'CAN'}
                    onChange={(value) => setLocal(prev => ({
                      ...prev,
                      order: { 
                        ...prev.order, 
                        customerDefaults: { 
                          ...prev.order.customerDefaults, 
                          priceList: value 
                        } 
                      }
                    }))}
                    placeholder="CAN"
                  />
                </div>
              </InlineStack>
            </BlockStack>
          </Card>

          <Card sectioned>
            <BlockStack gap="loose">
              <Text variant="headingMd" as="h3">Order Default Settings</Text>
              
              <InlineStack gap="400">
                <div style={{ flex: 1 }}>
                  <TextField
                    label="Default Location"
                    value={local.order.defaultLocation || '1'}
                    onChange={(value) => setLocal(prev => ({
                      ...prev,
                      order: { ...prev.order, defaultLocation: value }
                    }))}
                    placeholder="1"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <TextField
                    label="Price List"
                    value={local.order.defaultPriceList || 'CAN'}
                    onChange={(value) => setLocal(prev => ({
                      ...prev,
                      order: { ...prev.order, defaultPriceList: value }
                    }))}
                    placeholder="CAN"
                  />
                </div>
              </InlineStack>
              
              <TextField
                label="Terms Code"
                value={local.order.termsCode || '4'}
                onChange={(value) => setLocal(prev => ({
                  ...prev,
                  order: { ...prev.order, termsCode: value }
                }))}
                placeholder="4"
                helpText="Payment terms (e.g., 4 = Prepay)"
              />
              
              <InlineStack gap="400">
                <div style={{ flex: 1 }}>
                  <TextField
                    label="Payment Code"
                    value={local.order.paymentCode || 'CASH'}
                    onChange={(value) => setLocal(prev => ({
                      ...prev,
                      order: { ...prev.order, paymentCode: value }
                    }))}
                    placeholder="CASH"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <TextField
                    label="Shipping Charge Code"
                    value={local.order.shippingChargeCode || 'MC0001'}
                    onChange={(value) => setLocal(prev => ({
                      ...prev,
                      order: { ...prev.order, shippingChargeCode: value }
                    }))}
                    placeholder="MC0001"
                    helpText="Miscellaneous charge code for shipping"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <TextField
                    label="Gift Card Charge Code"
                    value={local.order.giftCardChargeCode || 'MC0002'}
                    onChange={(value) => setLocal(prev => ({
                      ...prev,
                      order: { ...prev.order, giftCardChargeCode: value }
                    }))}
                    placeholder="MC0002"
                    helpText="Miscellaneous charge code for gift cards"
                  />
                </div>
              </InlineStack>
            </BlockStack>
          </Card>

          <Card sectioned>
            <BlockStack gap="loose">
              <Text variant="headingMd" as="h3">Prepayment Configuration</Text>
              
              <Checkbox
                label="Apply Prepayments to Orders"
                checked={local.order.prepayment?.enabled || false}
                onChange={(value) => setLocal(prev => ({
                  ...prev,
                  order: {
                    ...prev.order,
                    prepayment: {
                      ...prev.order.prepayment,
                      enabled: value
                    }
                  }
                }))}
                helpText="When enabled, creates receipt batch and prepayment entries for each order"
              />
              
              {local.order.prepayment?.enabled && (
                <>
                  <InlineStack gap="400">
                    <div style={{ flex: 1 }}>
                      <TextField
                        label="Batch Name Prefix"
                        value={local.order.prepayment?.batchPrefix || 'WEB'}
                        onChange={(value) => setLocal(prev => ({
                          ...prev,
                          order: {
                            ...prev.order,
                            prepayment: {
                              ...prev.order.prepayment,
                              batchPrefix: value
                            }
                          }
                        }))}
                        placeholder="WEB"
                        helpText="Used to generate batch names (e.g., WEB 2026 W05)"
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Select
                        label="Batch Frequency"
                        options={[
                          { label: 'Daily', value: 'daily' },
                          { label: 'Weekly', value: 'weekly' },
                          { label: 'Monthly', value: 'monthly' }
                        ]}
                        value={local.order.prepayment?.batchFrequency || 'weekly'}
                        onChange={(value) => setLocal(prev => ({
                          ...prev,
                          order: {
                            ...prev.order,
                            prepayment: {
                              ...prev.order.prepayment,
                              batchFrequency: value
                            }
                          }
                        }))}
                        helpText="How often to create new receipt batches"
                      />
                    </div>
                  </InlineStack>
                  
                  <Text variant="bodyMd" as="p" tone="subdued">
                    Example batch names:
                    <ul style={{ marginTop: '8px', marginLeft: '20px' }}>
                      <li>Daily: WEB 2026 D0201</li>
                      <li>Weekly: WEB 2026 W05</li>
                      <li>Monthly: WEB 2026 M02</li>
                    </ul>
                  </Text>
                </>
              )}
            </BlockStack>
          </Card>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

